import React, { useState, useMemo, useEffect } from "react";
import SearchableDropdown from "../TicketComponents/SearchableDropdown";
import { useAuth } from "../../auth/AuthHandler";
import { useApiClient } from "../../service/API";

/* -------------------- Timezone helpers (SGT) -------------------- */

// Convert a Date to a Date representing same wall-clock time in SGT
const convertToSGT = (date) =>
  new Date(date.toLocaleString("en-US", { timeZone: "Asia/Singapore" }));

// Format a Date as 'YYYY-MM-DD' in SGT
const formatDateToSGT = (date) => {
  const d = convertToSGT(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// For <input type="date"> binding
const formatDateToInputValue = (date) => (date ? formatDateToSGT(date) : "");

/* -------------------- Component -------------------- */

export default function LeaveRequestForm({
  leaveAvailable,
  info,
  setInfo,
  handleRequestLeave,
  userList,
  leaveList = [],
}) {
  const apiClient = useApiClient();
  const { userRoles } = useAuth();
  const [errors, setErrors] = useState({
    reason: "",
    startDate: "",
    endDate: "",
    category: "",
    backup: "",
  });

  const [leaveAvailableList, setLeaveAvailableList] = useState();

  useEffect(() => {
    getLeaveAvailable();
  },[info.email]);

  // Keep leaveAvailableList in sync with the leaveAvailable prop for normal users.
  // Without this, if the parent fetches leaveAvailable asynchronously, the
  // useState initializer above captures a stale/empty value on first render
  // (e.g. right after a page refresh) and never updates again, leaving
  // categories permanently disabled even once the real balance arrives.
  useEffect(() => {
    if (!["BOMANAGER", "ADMIN"].some((role) => userRoles.includes(role))) {
      setLeaveAvailableList(leaveAvailable);
    }
  }, [leaveAvailable, userRoles]);

  const getLeaveAvailable = async () => {
    if (["BOMANAGER", "ADMIN"].some(role => userRoles.includes(role)) && info.email) {
      try {
        const response = await apiClient.get(`/users/leave-available/${info.email}`);
        if (response.status === 200) {
          setLeaveAvailableList(response.data);
        }
      } catch (ex) {
        console.error(ex);
      }
    }
  };

  useEffect(() => {
    getLeaveAvailable();
  },[info.email]);

  /* ---------- Index and analyzers ---------- */

  // Map<'YYYY-MM-DD', Entry[]>
  const leaveByDate = useMemo(() => {
    const m = new Map();
    for (const e of leaveList ?? []) {
      const key = (e.leavedate ?? e.leaveDate ?? "").slice(0, 10);
      if (!key) continue;
      (m.get(key) || m.set(key, []).get(key)).push(e);
    }
    return m;
  }, [leaveList]);

  const tp = (e) => ((e.timeperiod ?? e.timePeriod) ?? "").toString().toUpperCase();
  const cat = (e) => (e.category ?? "").toString().toUpperCase();
  const st  = (e) => (e.status ?? "").toString().toUpperCase();

  // Analyze entries for one date → flags
  function analyzeDay(entries = []) {
    const f = {
      fullApproved: false,
      halfAmApproved: false,
      halfPmApproved: false,
      fullPending: false,
      halfAmPending: false,
      halfPmPending: false,
      hasAny: entries.length > 0,
    };
    for (const e of entries) {
      const isHalf = cat(e).includes("HALF DAY");
      const P = tp(e); // 'AM' | 'PM' | '' | 'FULL'
      const S = st(e); // 'APPROVED' | 'END' | 'PENDING' | ...
      const approvedLike = S === "APPROVED" || S === "END";
      const pendingLike  = S === "PENDING";

      if (!isHalf || P === "" || P === "FULL") {
        if (approvedLike) f.fullApproved = true;
        if (pendingLike)  f.fullPending  = true;
      } else if (P === "AM") {
        if (approvedLike) f.halfAmApproved = true;
        if (pendingLike)  f.halfAmPending  = true;
      } else if (P === "PM") {
        if (approvedLike) f.halfPmApproved = true;
        if (pendingLike)  f.halfPmPending  = true;
      }
    }
    return f;
  }

  // Any-leave set for quick exact-day checks
  const anyLeaveDateSet = useMemo(() => new Set(leaveByDate.keys()), [leaveByDate]);

  /* ---------- Range capping (now blocks half-days when category is FULL-DAY) ---------- */

  // If category is FULL-DAY → treat ANY leave (full or half, approved or pending) as a blocker in the range.
  // If category is HALF-DAY → (end input is disabled anyway) but keep fully-blocked days for completeness.
  const blockedSetForRange = useMemo(() => {
    const s = new Set();
    const isHalf = !!info.category?.includes("HALF DAY");
    for (const [date, entries] of leaveByDate.entries()) {
      const f = analyzeDay(entries);
      const bothHalves =
        (f.halfAmApproved || f.halfAmPending) && (f.halfPmApproved || f.halfPmPending);

      if (!isHalf) {
        // FULL-DAY selection: any leave on that day blocks the range
        if (
          f.fullApproved || f.fullPending ||
          f.halfAmApproved || f.halfAmPending ||
          f.halfPmApproved || f.halfPmPending
        ) {
          s.add(date);
        }
      } else {
        // HALF-DAY selection: treat only fully blocked (full-day or both halves) as range blockers
        if (f.fullApproved || f.fullPending || bothHalves) {
          s.add(date);
        }
      }
    }
    return s;
  }, [leaveByDate, info.category]);

  const blockedSorted = useMemo(
    () => Array.from(blockedSetForRange).sort(),
    [blockedSetForRange]
  );

  // lowerBound + first blocked >= target
  const lowerBound = (arr, target) => {
    let lo = 0, hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (arr[mid] < target) lo = mid + 1; else hi = mid;
    }
    return lo;
  };
  const firstBlockedOnOrAfter = (startYMD) => {
    const i = lowerBound(blockedSorted, startYMD);
    return i < blockedSorted.length ? blockedSorted[i] : null;
  };

  // Dynamic max for end-date (FULL-DAY: blocks half-days in range now)
  const endMaxAttr = useMemo(() => {
    if (!info.startDate) return undefined;
    const s = formatDateToSGT(new Date(info.startDate));
    const fb = firstBlockedOnOrAfter(s);
    if (!fb) return undefined;
    const d = new Date(`${fb}T00:00:00`);
    d.setDate(d.getDate() - 1);
    return formatDateToSGT(d); // day before first blocked date
  }, [info.startDate, blockedSorted]);

  /* ---------- Selection-aware start/end blocking ---------- */

  // Block the chosen date depending on category/half-day availability
  const blockIfBooked = (dateObj, field) => {
    const ymd = formatDateToSGT(dateObj);
    const entries = leaveByDate.get(ymd) || [];
    const f = analyzeDay(entries);

    const isHalfDaySelection = !!info.category && info.category.includes("HALF DAY");

    if (!isHalfDaySelection) {
      // FULL-DAY category:
      // If there's ANY leave (full-day or half-day) on that date → block selection.
      if (anyLeaveDateSet.has(ymd)) {
        setErrors((prev) => ({ ...prev, [field]: "This date already has a leave." }));
        if (field === "startDate") { setInfo("startDate", null); setInfo("endDate", null); }
        else { setInfo("endDate", null); }
        return true;
      }
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return false;
    }

    // HALF-DAY category:
    // If full or both halves are taken/pending → block
    const bothHalves =
      (f.halfAmApproved || f.halfAmPending) && (f.halfPmApproved || f.halfPmPending);
    const fullBlocked = f.fullApproved || f.fullPending || bothHalves;
    if (fullBlocked) {
      setErrors((prev) => ({ ...prev, [field]: "This date is already fully blocked." }));
      if (field === "startDate") { setInfo("startDate", null); setInfo("endDate", null); }
      else { setInfo("endDate", null); }
      return true;
    }

    // Otherwise ensure requested period availability
    const amTaken = f.halfAmApproved || f.halfAmPending;
    const pmTaken = f.halfPmApproved || f.halfPmPending;
    const wanted = (info.timePeriod ?? "").toUpperCase();

    if ((wanted === "AM" && amTaken) || (wanted === "PM" && pmTaken)) {
      setErrors((prev) => ({ ...prev, [field]: `Selected period (${wanted}) is not available.` }));
      if (field === "startDate") { setInfo("startDate", null); setInfo("endDate", null); }
      else { setInfo("endDate", null); }
      return true;
    }

    setErrors((prev) => ({ ...prev, [field]: "" }));
    return false;
  };

  /* ---------- Half-day AM/PM radios disabled per selected start date ---------- */

  const selectedStartYMD = info.startDate ? formatDateToSGT(new Date(info.startDate)) : null;
  const startFlags = useMemo(
    () => analyzeDay(selectedStartYMD ? (leaveByDate.get(selectedStartYMD) || []) : []),
    [leaveByDate, selectedStartYMD]
  );

  const startFullBlocked = startFlags.fullApproved ||
                           startFlags.fullPending ||
                           ((startFlags.halfAmApproved || startFlags.halfAmPending) &&
                            (startFlags.halfPmApproved || startFlags.halfPmPending));

  const amDisabled = !!info.category?.includes("HALF DAY") &&
                     (startFullBlocked || startFlags.halfAmApproved || startFlags.halfAmPending);

  const pmDisabled = !!info.category?.includes("HALF DAY") &&
                     (startFullBlocked || startFlags.halfPmApproved || startFlags.halfPmPending);

  /* -------------------- Validation -------------------- */

  const validateFields = () => {
    const newErrors = {};
    let isValid = true;

    if (!info.backup) { newErrors.backup = "Back-up user is required"; isValid = false; }
    if (!info.startDate) { newErrors.startDate = "Start date is required"; isValid = false; }
    if (!info.endDate) { newErrors.endDate = "End date is required"; isValid = false; }
    if (!info.reason) { newErrors.reason = "Reason is required"; isValid = false; }
    if (!info.category) { newErrors.category = "Category is required"; isValid = false; }

    setErrors(newErrors);
    return isValid;
  };

  /* -------------------- Handlers -------------------- */

  const handleStartDateChange = (e) => {
    const picked = new Date(e.target.value);
    if (isNaN(picked)) return;

    // Selection-aware block (includes: if date has half-day & category is NOT half-day → block)
    if (blockIfBooked(picked, "startDate")) return;

    setInfo("startDate", convertToSGT(picked));

    if (info.category?.includes("HALF DAY")) {
      // End = start for half-day
      setInfo("endDate", convertToSGT(picked));
      // Keep error if chosen period is not available; radios are disabled accordingly
      if ((info.timePeriod === "AM" && amDisabled) || (info.timePeriod === "PM" && pmDisabled)) {
        setErrors((prev) => ({ ...prev, startDate: "Selected period is unavailable for this date." }));
      } else {
        setErrors((prev) => ({ ...prev, startDate: "" }));
      }
    } else {
      // Full-day: user picks range end
      setInfo("endDate", null);
    }
  };

  const handleEndDateChange = (e) => {
    const picked = new Date(e.target.value);
    if (isNaN(picked)) return;

    if (!info.startDate) {
      setErrors((prev) => ({ ...prev, endDate: "Pick start date first." }));
      return;
    }

    const s = formatDateToSGT(new Date(info.startDate));
    const eYMD = formatDateToSGT(picked);

    if (eYMD < s) {
      setErrors((prev) => ({ ...prev, endDate: "End date cannot be before the start date." }));
      return;
    }

    // Do not cross the first blocked date (FULL-DAY: half-days included)
    const fb = firstBlockedOnOrAfter(s);
    if (fb && eYMD >= fb) {
      setErrors((prev) => ({ ...prev, endDate: `End date cannot cross ${fb}.` }));
      setInfo("endDate", null);
      return;
    }

    // Disallow picking an exact day that already has ANY leave for FULL-DAY category
    if (!info.category?.includes("HALF DAY") && anyLeaveDateSet.has(eYMD)) {
      setErrors((prev) => ({ ...prev, endDate: "This date already has a leave." }));
      setInfo("endDate", null);
      return;
    }

    setErrors((prev) => ({ ...prev, endDate: "" }));
    setInfo("endDate", convertToSGT(picked));
  };

  const handleRequestLeaveSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    const startDate = new Date(info.startDate);
    const endDate = new Date(info.endDate);

    const updatedInfo = {
      ...info,
      startDate: formatDateToSGT(startDate),
      endDate: formatDateToSGT(endDate),
    };

    await handleRequestLeave(updatedInfo);
  };

  const handleSelectionChange = (selectedEmail) => setInfo("email", selectedEmail);
  const handleBackupSelectionChange = (selectedEmail) => setInfo("backup", selectedEmail);

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value.includes("HALF DAY")) {
      setInfo("duration", 0.5);
      if (!info.timePeriod) setInfo("timePeriod", "AM"); // default; radios will disable if taken
    } else {
      setInfo("duration", 0);
      setInfo("timePeriod", null);
    }
    setInfo("category", value);
    // Reset dates on category change to avoid inconsistent states
    setInfo("endDate", null);
    setInfo("startDate", null);
    setErrors((prev) => ({ ...prev, startDate: "", endDate: "" }));
  };

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const getBalanceKeyFromCategory = (categoryLabel = "") => {
    const u = categoryLabel.toUpperCase();
    if (u.includes("ANNUAL")) return "annual";
    if (u.includes("MEDICAL")) return "medical";
    if (u.includes("HOSPITAL")) return "hospital";
    if (u.includes("UNPAID")) return "unpaid";
    return null;
  };

  const isCategoryDisabledByBalance = (categoryLabel) => {
    const key = getBalanceKeyFromCategory(categoryLabel);
    if (!key) return false;
    if (key === "unpaid") return false;

    const balance = toNum(leaveAvailableList?.[key]);
    const isHalfDayCategory = categoryLabel.toUpperCase().includes("HALF DAY");

    // No balance at all
    if (balance <= 0) return true;

    // Only half day left
    if (balance === 0.5) {
      return !isHalfDayCategory; // disable full-day, allow half-day
    }

    // 1 day or more
    return false;
  };

  /* -------------------- Render -------------------- */

  return (
    <div className="leave-request-form">
      <h5>Leave request form</h5>

      <form>
        {/* Leave Category */}
        {["BOMANAGER", "ADMIN"].some((role) => userRoles.includes(role)) && (
          <div className="col">
            <div className="mb-3 row">
              <label htmlFor="request-by" className="col-sm-4 col-form-label">
                User Email:
              </label>
              <div className="col-sm-8">
                <SearchableDropdown
                  id="request-by"
                  options={userList}
                  selectedValue={info.email || ""}
                  onSelectedChange={handleSelectionChange}
                  placeholder="Select user"
                />
              </div>
            </div>
          </div>
        )}

        <div className="col">
          <div className="mb-3 row">
            <label htmlFor="leaveCategory" className="col-sm-4 col-form-label">
              Leave Category:
            </label>
            <div className="col-sm-8">
              <select
                id="leaveCategory"
                className={`form-select ${errors.category ? "is-invalid" : ""}`}
                value={info.category || ""}
                onChange={handleCategoryChange}
              >
                <option value="">--- SELECT 1 ---</option>
                {[
                  "RESIGN/TERMINATION",
                  "ANNUAL LEAVE",
                  "ANNUAL LEAVE - HALF DAY",
                  "MEDICAL LEAVE",
                  "MEDICAL LEAVE - HALF DAY",
                  "HOSPITAL LEAVE",
                  "HOSPITAL LEAVE - HALF DAY",
                  "UNPAID LEAVE",
                  "UNPAID LEAVE - HALF DAY"
                ].filter(
                  selection => 
                    ["BOMANAGER", "ADMIN"].some((role) => userRoles.includes(role))
                    || selection !== "RESIGN/TERMINATION"
                  )
                  .map((listValue) => (
                  <option key={listValue} value={listValue} disabled={isCategoryDisabledByBalance(listValue)}>
                    {listValue}
                  </option>
                ))}
              </select>
              {errors.category && (
                <div className="invalid-feedback">{errors.category}</div>
              )}
            </div>
          </div>
        </div>

        {/* Half-day AM/PM radios — disabled per selected start date */}
        {info.category?.includes("HALF DAY") && (
          <div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="timePeriod"
                id="radioAM"
                onChange={() => setInfo("timePeriod", "AM")}
                checked={info.timePeriod === "AM"}
                disabled={amDisabled}
              />
              <label className="form-check-label" htmlFor="radioAM">
                AM {amDisabled && selectedStartYMD ? "(unavailable)" : ""}
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="timePeriod"
                id="radioPM"
                onChange={() => setInfo("timePeriod", "PM")}
                checked={info.timePeriod === "PM"}
                disabled={pmDisabled}
              />
              <label className="form-check-label" htmlFor="radioPM">
                PM {pmDisabled && selectedStartYMD ? "(unavailable)" : ""}
              </label>
            </div>
          </div>
        )}

        {/* Start Date */}
        <div className="col">
          <div className="mb-3 row">
            <label htmlFor="startDate" className="col-sm-4 col-form-label">
              Start date:
            </label>
            <div className="col-sm-8">
              <input
                type="date"
                className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
                id="startDate"
                disabled={info.category === null || info.category === ""}
                onChange={handleStartDateChange}
                value={formatDateToInputValue(info.startDate)}
              />
              {errors.startDate && (
                <div className="invalid-feedback">{errors.startDate}</div>
              )}
            </div>
          </div>
        </div>

        {/* End Date */}
        <div className="col">
          <div className="mb-3 row">
            <label htmlFor="endDate" className="col-sm-4 col-form-label">
              End date:
            </label>
            <div className="col-sm-8">
              <input
                type="date"
                className={`form-control ${errors.endDate ? "is-invalid" : ""}`}
                id="endDate"
                disabled={
                  info.category === null ||
                  info.category === "" ||
                  info.category.includes("HALF DAY")
                }
                onChange={handleEndDateChange}
                value={formatDateToInputValue(info.endDate)}
                min={formatDateToInputValue(info.startDate)}
                max={endMaxAttr /* day before first blocked date (half-days included for FULL-DAY) */}
              />
              {errors.endDate && (
                <div className="invalid-feedback">{errors.endDate}</div>
              )}
            </div>
          </div>
        </div>

        {/* Back-Up */}
        <div className="col">
          <div className="mb-3 row">
            <label htmlFor="backup" className="col-sm-4 col-form-label">
              Back-Up user:
            </label>
            <div className="col-sm-8">
              <div className={`${errors.backup ? "is-invalid" : ""}`}>
                <SearchableDropdown
                  id="backup"
                  options={[
                    { email: "N/A", fullname: "N/A" },
                    ...userList.filter((user) => user.email !== info.email),
                  ].filter(
                  selection => 
                    ["BOMANAGER", "ADMIN"].some((role) => userRoles.includes(role))
                    || selection.email !== "N/A"
                  )}
                  selectedValue={info.backup || ""}
                  onSelectedChange={handleBackupSelectionChange}
                  placeholder="Select user"
                />
              </div>
              {errors.backup && (
                <div className="invalid-feedback">{errors.backup}</div>
              )}
            </div>
          </div>
        </div>

        {/* Reason */}
        <div className="mb-3 row">
          <label htmlFor="inputReason" className="col-sm-4 col-form-label">
            Reason:
          </label>
          <div className="col-sm-8">
            <textarea
              name="reason"
              className={`form-control ${errors.reason ? "is-invalid" : ""}`}
              id="inputReason"
              rows="3"
              value={info.reason || ""}
              onChange={(e) => setInfo("reason", e.target.value)}
            />
            {errors.reason && (
              <div className="invalid-feedback">{errors.reason}</div>
            )}
          </div>
        </div>

        {/* Duration */}
        <div className="mb-3 row">
          <label htmlFor="duration" className="col-sm-4 col-form-label">
            Duration:
          </label>
          <div className="col-sm-8">
            <input
              name="duration"
              className="form-control"
              id="duration"
              value={info.duration || ""}
              disabled
            />
          </div>
        </div>

        <button
          type="button"
          className="btn btn-primary ms-2"
          onClick={handleRequestLeaveSubmit}
        >
          {["BOUSER"].some((role) => userRoles.includes(role))
            ? "Request Leave"
            : "Create Leave"}
        </button>
      </form>
    </div>
  );
}
